import { z } from 'zod'

import { statesSchema, type State } from './states'

export { type State }

// License Number Type
export const licenseNumberSchema = z
  .string()
  .min(4, 'License number must be at least 4 characters')
  .max(12, 'License number must not exceed 12 characters')
  .regex(/^[a-zA-Z0-9.:]+$/, 'Only letters, numbers, colons and periods are allowed')
  .regex(/^\S*$/, 'Spaces are not allowed')

export type LicenseNumber = z.infer<typeof licenseNumberSchema>

// License Types
export type License = {
  id: number
  state: State
  licenseNumber: LicenseNumber | null
  status: 'approved' | 'pending' | 'information_requested' | 'denied'
  imageUrl?: string
  notes?: string
}

export type StateLicensePair = {
  state: State
  licenseNumber: LicenseNumber
}

// License Status Types
export type LicenseStatus = {
  text: string
  icon: string | React.ForwardRefExoticComponent<any>
  iconProps?: { isVerified: boolean }
  iconColorClass: string
  isComponent: boolean
}

export const stateLicensePairSchema = z.object({
  state: statesSchema,
  licenseNumber: licenseNumberSchema,
})

// MLO specific schema
export const mloLicenseFormSchema = z.object({
  stateLicensePairs: z.array(statesSchema).min(1, 'At least one state must be selected'),
  nmlsNumber: licenseNumberSchema.min(4, 'NMLS number must be at least 4 characters'),
})

// General user schema
export const generalLicenseFormSchema = z.object({
  stateLicensePairs: z
    .array(
      z.object({
        state: statesSchema,
        licenseNumber: licenseNumberSchema,
      }),
    )
    .min(1, 'At least one state must be selected'),
  nmlsNumber: z.literal(''),
})

// Export types for both schemas
export type MLOLicenseFormData = z.infer<typeof mloLicenseFormSchema>
export type GeneralLicenseFormData = z.infer<typeof generalLicenseFormSchema>
export type LicenseFormData = MLOLicenseFormData | GeneralLicenseFormData

export const updateLicenseSchema = z.object({
  licenseNumber: licenseNumberSchema,
})

export type UpdateLicenseFormData = z.infer<typeof updateLicenseSchema>

// License Status Utilities
export const getLicenseStatusPriority = (status: string): number => {
  switch (status) {
    case 'approved':
      return 1
    case 'pending':
      return 2
    case 'information_requested':
      return 3
    case 'denied':
      return 4
    default:
      return 5
  }
}

// Filter available states by removing already licensed states
export const getAvailableStates = (
  existingLicenses: License[] | undefined,
  stateOptions: Record<State, { label: string }>,
) => {
  if (!existingLicenses?.length) return stateOptions

  const licensedStates = new Set(existingLicenses.map((license) => license.state))
  return Object.entries(stateOptions).reduce((acc, [state, value]) => {
    if (!licensedStates.has(state as State)) {
      acc[state as State] = value
    }
    return acc
  }, {} as Record<State, { label: string }>)
}
